import * as React from "react";
import { useScroll } from "@react-three/drei";
import type { ScrollLayoutStore, ScrollMeasurement } from "./scrollLayout";

/** Rect-difference measurement: getBoundingClientRect includes drei's
 *  translate3d on the overlay, but subtracting the content root's rect cancels
 *  it, leaving static offsets. */
export function readMeasurement(
  el: HTMLElement,
  content: HTMLElement,
): ScrollMeasurement {
  const contentRect = content.getBoundingClientRect();
  const anchors: Record<string, number> = {};
  content.querySelectorAll<HTMLElement>("[data-scroll-anchor]").forEach((node) => {
    const key = node.dataset.scrollAnchor;
    if (key) anchors[key] = node.getBoundingClientRect().top - contentRect.top;
  });
  return { vh: el.clientHeight, contentHeight: contentRect.height, anchors };
}

type Props = {
  store: ScrollLayoutStore;
  /** The pages value currently passed to ScrollControls. Changing it makes
   *  drei detach/re-attach its scroll container and reset scrollTop to 1 —
   *  this component saves the position before the change and restores it after
   *  drei's effect has run (double rAF). */
  pages: number;
};

export function ScrollLayoutMeasurer({ store, pages }: Props) {
  const scroll = useScroll();

  // --- measure overlay content ------------------------------------------
  React.useEffect(() => {
    const el = scroll?.el as HTMLElement | undefined;
    const fixed = scroll?.fixed as HTMLElement | undefined;
    if (!el || !fixed) return undefined;
    let raf = 0;
    let ro: ResizeObserver | null = null;
    const measure = (content: HTMLElement) => {
      const m = readMeasurement(el, content);
      // While drei re-attaches its scroll container after a `pages` prop
      // change, el/content are momentarily detached and measure as 0 — writing
      // that would reset pages to the fallback and remount drei again, looping
      // forever. Skip degenerate measurements.
      if (m.vh <= 0 || m.contentHeight <= 0) return;
      store.set(m);
    };
    const attach = () => {
      // drei renders the html overlay in a detached root that commits async —
      // poll until the content wrapper exists.
      const content = fixed.querySelector<HTMLElement>("[data-scroll-content]");
      if (!content) {
        raf = requestAnimationFrame(attach);
        return;
      }
      measure(content);
      if (typeof ResizeObserver !== "undefined") {
        ro = new ResizeObserver(() => measure(content));
        ro.observe(content); // CMS content / fonts / images growing
        ro.observe(el); // viewport (incl. mobile URL-bar) changes
      }
    };
    attach();
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      // Deliberately NOT store.set(null): this cleanup also runs on every
      // `pages` change (drei re-memoizes its scroll state), and clearing the
      // store would bounce pages back to the fallback. The last good
      // measurement stays; a remount simply re-measures over it.
    };
  }, [scroll, store]);

  // --- preserve scroll position across drei's pages remount ---------------
  // Preserve the scroll *fraction* (drei's offset), NOT raw pixels: a `pages`
  // change usually comes from a viewport resize, which changes vh and the
  // total scrollable height, so the same pixel offset maps to a different
  // fraction. Every scroll-driven scene reads `scroll.offset` (a 0..1
  // fraction) to place the camera / 3D models, so restoring raw px would land
  // the choreography at the wrong stage after a resize (e.g. the /spirit-box model
  // jumping sides or vanishing). Restoring the fraction keeps the user — and
  // the choreography — at the same logical position.
  const savedFraction = React.useRef(0);
  React.useEffect(() => {
    const el = scroll?.el as HTMLElement | undefined;
    if (!el) return undefined;
    const frac = savedFraction.current;
    let raf1 = 0;
    let raf2 = 0;
    if (frac > 0.0001) {
      // drei's own effect (which resets scrollTop to 1) runs after this child
      // effect; defer two frames so the restore lands last — and so el's new
      // scrollHeight (the resized page) is in place when we map the fraction.
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          const max = el.scrollHeight - el.clientHeight;
          if (max > 0) el.scrollTop = frac * max;
        });
      });
    }
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      // Capture drei's tracked offset from the OLD (pre-resize) scroll state;
      // it reflects the last frame's fraction independent of imperative DOM
      // timing. Fall back to measuring the element if offset is unavailable.
      const offset = (scroll as { offset?: number } | undefined)?.offset;
      if (typeof offset === "number") {
        savedFraction.current = offset;
      } else {
        const max = el.scrollHeight - el.clientHeight;
        savedFraction.current = max > 0 ? el.scrollTop / max : 0;
      }
    };
  }, [pages, scroll]);

  return null;
}
